// A linha unica que substitui a pilha de faixas quando ha 2+ alertas no ar.
// Clicar em QUALQUER ponto dela (pastilha inclusive) expande a lista completa — uma acao so,
// sem alvo escondido. A navegacao pra tela de cada alerta continua sendo da faixa completa.
import { useLayoutEffect, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { corMaisGrave, useAlertasCtx, type AlertaRegistro } from './AlertasContexto';

// Quantas pastilhas aparecem: as que CABEM na largura (pedido do Ruan, 05/10/2026). Ate aqui era
// um teto fixo de 6, e num monitor de 1920px a linha usava metade da largura enquanto 5 avisos
// ficavam no "+5" — o de codigo de barras, recem-criado, nasceu la dentro e ele nao o viu.
// Mede as pastilhas numa copia invisivel (mesmo componente, mesma fonte) e corta onde a proxima
// nao cabe, reservando lugar pro "+N". ResizeObserver refaz ao mudar a janela; no tablet cabem
// menos e o resto segue no "+N", como antes.
const GAP = 6;
const RESERVA_MAIS_N = 40;

function Pastilha({ a }: { a: AlertaRegistro }) {
  return (
    <span
      style={{
        background: a.bg,
        border: `1px solid ${a.borda}`,
        color: '#000',
        padding: '2px 9px',
        borderRadius: 11,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        fontSize: 13,
      }}
    >
      {a.rotulo}
      {a.qtd > 1 && (
        <b style={{ background: 'rgba(0,0,0,0.22)', borderRadius: 8, padding: '0 5px', marginLeft: 4 }}>
          {a.qtd}
        </b>
      )}
    </span>
  );
}

export function BarraAlertasResumo() {
  const ctx = useAlertasCtx();
  const contRef = useRef<HTMLSpanElement>(null);
  const medRef = useRef<HTMLSpanElement>(null);
  const [cabem, setCabem] = useState(6);

  const ativosLista = ctx?.ativos ?? [];
  const desenha = !!ctx && ativosLista.length >= 2 && ctx.preferenciaRecolhida;
  // Muda quando entra/sai aviso ou o numero de um deles muda (a pastilha alarga com o contador).
  const chave = ativosLista.map((a) => `${a.id}:${a.qtd}`).join('|');

  // Hooks antes de qualquer return (regra dos hooks).
  useLayoutEffect(() => {
    const cont = contRef.current;
    const med = medRef.current;
    if (!desenha || !cont || !med) return;
    const calcular = () => {
      const largura = cont.clientWidth;
      const filhos = Array.from(med.children) as HTMLElement[];
      let usado = 0;
      let n = 0;
      for (let i = 0; i < filhos.length; i++) {
        const prox = usado + (n > 0 ? GAP : 0) + filhos[i]!.offsetWidth;
        const sobram = filhos.length - (i + 1);
        if ((sobram > 0 ? prox + GAP + RESERVA_MAIS_N : prox) > largura) break;
        usado = prox;
        n++;
      }
      setCabem(Math.max(1, n));
    };
    calcular();
    const ro = new ResizeObserver(calcular);
    ro.observe(cont);
    return () => ro.disconnect();
  }, [desenha, chave]);

  if (!ctx) return null;
  const { ativos, alternar } = ctx;
  // Faixa unica se desenha inteira; a barra so existe pra desempilhar 2 ou mais.
  if (!desenha) return null;

  const { borda } = corMaisGrave(ativos);
  const visiveis = ativos.slice(0, cabem);
  const ocultos = ativos.length - visiveis.length;

  return (
    <div
      onClick={alternar}
      title="Ver os avisos abertos"
      style={{
        position: 'relative',
        background: '#cbd5e1',
        borderLeft: `4px solid ${borda}`,
        color: '#000',
        padding: '7px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        fontSize: 13,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <AlertTriangle size={16} />
      <b style={{ whiteSpace: 'nowrap' }}>{ativos.length} avisos:</b>
      <span ref={contRef} style={{ display: 'flex', gap: GAP, alignItems: 'center', overflow: 'hidden', flex: 1, minWidth: 0 }}>
        {visiveis.map((a) => <Pastilha key={a.id} a={a} />)}
        {ocultos > 0 && <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>+{ocultos}</span>}
      </span>
      {/* Copia invisivel de TODAS as pastilhas, so pra medir a largura de cada uma. */}
      <span
        ref={medRef}
        aria-hidden
        style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', display: 'flex', gap: GAP, whiteSpace: 'nowrap', left: 0, top: 0 }}
      >
        {ativos.map((a) => <Pastilha key={a.id} a={a} />)}
      </span>
      <span
        style={{
          marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
          fontWeight: 700, whiteSpace: 'nowrap',
        }}
      >
        ver todos <ChevronDown size={15} />
      </span>
    </div>
  );
}

/** Barrinha de "recolher" que fecha a lista expandida. Fica logo abaixo da ultima faixa. */
export function RodapeRecolherAlertas() {
  const ctx = useAlertasCtx();
  if (!ctx) return null;

  const { ativos, preferenciaRecolhida, alternar } = ctx;
  if (ativos.length < 2 || preferenciaRecolhida) return null;

  return (
    <div
      onClick={alternar}
      title="Recolher os avisos numa linha so"
      style={{
        background: '#94a3b8',
        borderLeft: '4px solid #475569',
        color: '#000',
        padding: '4px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 6,
        fontSize: 12,
        fontWeight: 700,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      recolher avisos <ChevronUp size={14} />
    </div>
  );
}
